import { Tag } from 'antd';
import { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { DraggableActionsButton } from '../../common/option-list/style/DraggableActionsButton.tsx';
import { WidgetOptionStructure } from '../../common/option-list/style/WidgetOptionStructure.tsx';
import {
  type AdvancedSelectColumnDatatype,
  type IAdvancedSelectColumns,
} from '../IAdvancedSelectColumns.ts';
import { AdvancedSelectRichTextValue } from '../utils/AdvancedSelectRichTextValue.tsx';
import { formatValueByDatatype } from '../utils/formatValueByDatatype.ts';

const OptionContainer = styled.div`
  width: 100%;
  overflow-x: auto;

  .ant-tag {
    display: inline-flex;
    align-items: baseline;
    max-width: 100%;
  }
`;

const RichTextOptionTag = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  max-width: 100%;
  margin-inline-end: 8px;
  margin-bottom: 8px;
  padding: 0 7px;
  color: ${props => props.theme.colorText ?? 'rgba(0, 0, 0, 0.88)'};
  font-size: 12px;
  line-height: 20px;
  background: ${props => props.theme.colorFillQuaternary ?? '#fafafa'};
  border: 1px solid ${props => props.theme.colorBorder ?? '#d9d9d9'};
  border-radius: 4px;

  > strong {
    flex: 0 0 auto;
  }
`;

interface IFormattedValue {
  id: string;
  label: string;
  value: string;
  rawValue: string | null;
  type: AdvancedSelectColumnDatatype;
}

export interface IAdvancedSelectOptionValueViewProps {
  item: IWidgetAdvancedOption | null;
  columns?: IAdvancedSelectColumns[];
  listeners?: any;
  onEdit?: (e: any) => void;
  onRemove?: (e: any) => void;
  onAdd?: (e: any) => void;
}

/**
 * Visualizzazione personalizzata per le opzioni dei widget AdvancedSelect (singola e multipla).
 * Mostra le colonne come tag formattati con il valore appropriato.
 */
export function AdvancedSelectOptionValueView(
  props: IAdvancedSelectOptionValueViewProps,
) {
  const { item, columns, listeners, ...actions } = props;

  const formattedValues = useMemo(() => {
    const hasValidColumns = columns != null && columns.length > 0;
    if (item?.values == null || !hasValidColumns) {
      return [];
    }

    return columns
      .map(col => {
        const value = item.values?.[col.id];

        return {
          id: col.id,
          label: col.label,
          value: formatValueByDatatype(value, col.type) || '-',
          rawValue: value,
          type: col.type,
        };
      })
      .filter((v): v is IFormattedValue => v != null);
  }, [item, columns]);

  return (
    <WidgetOptionStructure
      listeners={listeners}
      actions={<DraggableActionsButton {...actions} />}
    >
      <OptionContainer>
        {formattedValues.map(fv => (
          <Fragment key={fv.id}>
            {fv.type === Datatype.RichText ? (
              <RichTextOptionTag>
                <strong>{fv.label}:</strong>
                <AdvancedSelectRichTextValue
                  value={fv.rawValue}
                  emptyValue="-"
                />
              </RichTextOptionTag>
            ) : (
              <Tag>
                <strong>{fv.label}:</strong> {fv.value}
              </Tag>
            )}
          </Fragment>
        ))}
      </OptionContainer>
    </WidgetOptionStructure>
  );
}
